import { init } from '@sentry/react';
import 'proxy-polyfill';
import ReactDOM from 'react-dom/client';
import ReactPixel from 'services/facebookPixel';
import { clearStore, isIndexedDBConnectionError } from 'services/idb-store';
import { logError } from 'services/logging';
import { registerServiceWorker } from 'services/registerServiceWorker';
import { createServiceWorkerUpdater } from 'services/serviceWorkerUpdater';
import 'typeface-nunito';
import 'typeface-roboto';
import 'typeface-roboto-mono';
import { isLocal } from 'config/env';
import { config } from 'config/environments';
import { isDynamicImportChunkError } from 'utils/dynamicImportError';
import { isHeadless } from 'utils/headless';
import { getSentryInitOptions } from 'utils/sentry';
import {
  recordServiceWorkerForceReload,
  registerPrintBreadcrumbs,
  restoreServiceWorkerForceReloadBreadcrumb,
} from 'utils/session-instrumentation';
import App from './App';
import './index.css';

if (!isLocal) {
  init(
    getSentryInitOptions({
      dsn: config.sentryDSN,
    }),
  );

  restoreServiceWorkerForceReloadBreadcrumb();
  registerPrintBreadcrumbs();

  const FACEBOOK_TRACKING_PIXEL = '1119188959501360';
  ReactPixel.init(FACEBOOK_TRACKING_PIXEL);
}

const root = ReactDOM.createRoot(document.getElementById('root')!);
root.render(<App />);

// Global handler for failed dynamic imports (common after deploys when old HTML references deleted chunks)
// Force a full reload to get fresh HTML with correct chunk references
window.addEventListener('unhandledrejection', (event) => {
  const error = event.reason;

  if (isDynamicImportChunkError(error)) {
    event.preventDefault(); // Prevent default error logging since we're handling it
    console.warn('Detected failed dynamic import, likely due to stale HTML after deploy. Reloading...', error);

    // Force a hard reload to bypass cache and get fresh HTML
    window.location.reload();
    return;
  }

  // Handle IndexedDB connection lost - refresh restores the connection
  if (isIndexedDBConnectionError(error)) {
    event.preventDefault();
    window.location.reload();
  }
});

const isIOS = () => /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream;
const FIFTEEN_MINUTES = 15 * 60 * 1000;

if (!isIOS() && !isHeadless()) {
  registerServiceWorker((registration) => {
    // Drop cached query payloads tied to the previous app version before the new SW activates.
    if (registration.installing || registration.waiting) {
      clearStore();
    }

    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('message', (event) => {
        if (event.data?.type === 'FORCE_RELOAD') {
          recordServiceWorkerForceReload();
          window.location.reload();
        }
      });
    }

    const tryUpdate = createServiceWorkerUpdater(registration);

    setInterval(tryUpdate, FIFTEEN_MINUTES);

    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState !== 'visible') return;
      if (!('serviceWorker' in navigator) || !navigator.serviceWorker.controller) return;
      tryUpdate();
    });
  }).catch((err: any) => {
    logError(new Error(`Service worker registration failed. Error: ${err.message}`));
  });
}
